import Papa from 'papaparse';
import { makeId } from "./id";

/**
 * Process uploaded files (CSV/JSON) and return structured data for creating a data view
 * @param {File} file - The uploaded file
 * @returns {Promise<Object>} - Promise resolving to structured data object
 */
export const processUploadedFile = (file) => {
  return new Promise((resolve, reject) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const newId = makeId(10);

    if (fileExtension === 'csv') {
      // Process CSV file
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            console.error('CSV parsing errors:', results.errors);
          }
          
          const structuredData = createDataViewStructure(
            file.name.replace('.csv', ''),
            results.data,
            'csv'
          );
          resolve(structuredData);
        },
        error: (error) => {
          reject(new Error(`CSV parsing failed: ${error.message}`));
        }
      });
    } else if (fileExtension === 'json') {
      // Process JSON file
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          
          // Handle different JSON structures
          let dataArray;
          if (Array.isArray(jsonData)) {
            dataArray = jsonData;
          } else if (jsonData.data && Array.isArray(jsonData.data)) {
            dataArray = jsonData.data;
          } else if (typeof jsonData === 'object') {
            // Convert single object to array
            dataArray = [jsonData];
          } else {
            throw new Error('Unsupported JSON structure');
          }
          
          const structuredData = createDataViewStructure(
            file.name.replace('.json', ''),
            dataArray,
            'json'
          );
          resolve(structuredData);
        } catch (error) {
          reject(new Error(`JSON parsing failed: ${error.message}`));
        }
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsText(file);
    } else {
      reject(new Error(`Unsupported file type: ${fileExtension}`));
    }
  });
};

/**
 * Create a structured data view with title, chart, and table components
 * @param {string} fileName - Name of the uploaded file (without extension)
 * @param {Array} data - Parsed data array
 * @param {string} fileType - Type of file (csv/json)
 * @returns {Object} - Structured data view object
 */
const createDataViewStructure = (fileName, data, fileType) => {
  if (!data || data.length === 0) {
    throw new Error('No data found in file');
  }
  
  // Get column names from the first row
  const firstRow = data[0];
  const columns = Object.keys(firstRow);
  
  // Detect numeric columns for charting
  const numericColumns = columns.filter(col => {
    const colValues = data.map(row => String(row[col] !== undefined && row[col] !== null ? row[col] : ''));
    const numericCount = colValues.filter(cell => {
      // Remove currency symbols, commas, percentages, and whitespace
      const clean = cell.replace(/[$,%\s]/g, '');
      return clean !== '' && !isNaN(Number(clean));
    }).length;
    return numericCount / colValues.length > 0.5;
  });
  
  const categoricalColumns = columns.filter(col => !numericColumns.includes(col));

  let titleHtml;
  if (fileName.includes(" - ")) {
    const lastIndex = fileName.lastIndexOf(" - ");
    const docName = fileName.substring(0, lastIndex);
    const sheetName = fileName.substring(lastIndex + 3);
    titleHtml = `<h1>${docName}</h1><h6><i>${sheetName}</i></h6>`;
  } else {
    titleHtml = `<h1>${fileName}</h1>`;
  }

  // Create components for the data view (following the actual structure from ViewPage)
  const components = [
    // Title/Description component - text-editor type
    {
      type: "text-editor",
      id: makeId(10),
      content: `${titleHtml}<p>Data imported from ${fileName}.${fileType} containing ${data.length} records with ${columns.length} fields.</p>`,
      cards: 1 // Standard card size
    },
    {
      type: "text-editor",
      id: makeId(10),
      content: `<h3>All Fields:</h3><p>${columns.join(', ')}</p>${numericColumns.length > 0 ? `<br><h3>Numeric fields:</h3> <p>${numericColumns.join(', ')}</p>` : ''}`,
      cards: 2 // Standard card size
    },
    // Table component with Flexmonster - data-visualization type
    {
      type: "data-visualization",
      id: makeId(10),
      content: JSON.stringify({
        dataSource: {
          type: "json",
          data: data
        },
        slice: {
          reportFilters: [],
          rows: categoricalColumns.length > 0 ? [{ uniqueName: categoricalColumns[0] }] : [],
          columns: [],
          measures: numericColumns.slice(0, 5).map(col => ({ uniqueName: col }))
        },
        options: {
          viewType: "charts",
          chart: {
            type: "line",
            multipleMeasures: true,
            showDataLabels: false,
            // title: categoricalColumns[0]+" "+"by "+ numericColumns.slice(0, 5).join(", "),
          },
          grid: {
            showFilter: true,
            showHeaders: true,
            showHierarchies: true,
            showReportFiltersArea: true,
            showGrandTotals: "off",

          },
          formats: [{
            name: "",               // Leaving name empty applies it to ALL measures
            thousandsSeparator: ",", // Comma for thousands
            decimalSeparator: ".",   // Dot for decimals
            // decimalPlaces: 2         // Force 2 decimal places
          }],


        }
      }),
      cards: 3
    }
  ];

  // Add chart component if we have numeric data and a categorical column
  if (numericColumns.length >= 1 && columns.length > numericColumns.length) {
    
    const chartComponent = {
      type: "data-visualization",
      id: makeId(10),
      content: JSON.stringify({
        dataSource: {
          type: "json",
          data: data
        },
        slice: {
          reportFilters: [],
          rows: categoricalColumns.length > 1 ? [{ uniqueName: categoricalColumns[1] }] : [],
          columns: categoricalColumns.length > 2 ? [{ uniqueName: categoricalColumns[2] }] : [],
          measures: numericColumns.slice(0, 2).map(col => ({ uniqueName: col })) // Use up to 2 numeric columns for chart
        },
        options: {
          viewType:"charts",
          chart: {
            type: "stacked_column"
          },
          showDataLabels: "true",
        },
        formats: [{
          name: "",               // Leaving name empty applies it to ALL measures
          thousandsSeparator: ",", // Comma for thousands
          decimalSeparator: ".",   // Dot for decimals
          // decimalPlaces: 2         // Force 2 decimal places
        }]


      }),
      cards: 3
    };

    components.push(chartComponent);   

    const flatTable = {
      type: "data-visualization",
      id: makeId(10),
      content: JSON.stringify({
        dataSource: {
          type: "json",
          data: data
        },
        options: {
          viewType: "grid",
          grid: {
            type: "flat"
          }
        },
        // For a flat table, the slice is simplified.
        // It will show all columns from the data source by default.
        slice: {},
        formats: [{
          name: "",               // Leaving name empty applies it to ALL measures
          thousandsSeparator: ",", // Comma for thousands
          decimalSeparator: ".",   // Dot for decimals
          // decimalPlaces: 2         // Force 2 decimal places
        }]

      }),
      cards: 3
    };
    components.push(flatTable);
  }

  return {
    title: fileName,
    description: `Automatically generated data view from ${fileName}.${fileType}`,
    contents: components,
    fileData: {
      fileName: `${fileName}.${fileType}`,
      fileType: fileType,
      recordCount: data.length,
      columns: columns,
      numericColumns: numericColumns
    }
  };
};

/**
 * Generate a sample data structure for testing
 * @returns {Object} - Sample structured data
 */
export const generateSampleData = () => {
  const sampleData = [
    { month: 'January', sales: 1000, revenue: 50000, customers: 250 },
    { month: 'February', sales: 1200, revenue: 60000, customers: 300 },
    { month: 'March', sales: 800, revenue: 40000, customers: 200 },
    { month: 'April', sales: 1500, revenue: 75000, customers: 375 },
    { month: 'May', sales: 1800, revenue: 90000, customers: 450 }
  ];

  return createDataViewStructure('sample-data', sampleData, 'json');
};